import { Add, Error, FilterList } from '@mui/icons-material';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import { pluralize } from '@prismamedia/one-components';
import type { FC } from 'react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from 'react';
import { useParams } from 'react-router-dom';
import { paths } from '../../../../routing/Router/paths';
import { canCreateCategory } from '../../../../utils/auth';
import { useQueryParams } from '../../../../utils/useUriHelpers';
import { CategoriesContext } from '../../categories.context';
import type { CategoryList } from '../../types';
import { navigateTo, getBrandCategoriesWithPath } from '../../utils';
import { useQuery, NetworkStatus } from '@apollo/client';
import { GET_PAGE_CATEGORIES } from '../../apollo/queries/categories.web.graphql';
import {
  CategoryStatus,
  BrandKey,
} from '../../../../__generated__/queries-web';
import { BreadCrumbs } from './components/BreadCrumbs';
import { CategoryCard } from './components/CategoryCard';
import { Filter } from './components/Filter';
import { INITIAL_QUERY_PARAMS } from './components/Filter/constants';
import { CreationRightsWrapper } from '../../components/CreationRightsWrapper';

// Completely isolated BreadCrumbs to prevent any parent re-renders
const IsolatedBreadCrumbs = memo(BreadCrumbs, (prevProps, nextProps) => {
  // Only re-render if breadcrumbsData reference actually changed
  return prevProps.breadcrumbsData === nextProps.breadcrumbsData;
});
import {
  ACTIVE_FILTER_PREFIX,
  ACTIVE_FILTER_SUFFIX,
  ERROR_BUTTON_RELOAD_LABEL,
  ERROR_HAPPENED_TEXT,
  NO_RESULT_TEXT,
} from './constants';
import { useStyles } from './styles';
import { getActiveFilterQuantity } from './utils';

const ITEMS_PER_PAGE = 30;
const VALID_CATEGORY_STATUSES = new Set(Object.values(CategoryStatus));

const List: FC = () => {
  const routeParams = useParams();
  const { brandKey, format } = useContext(CategoriesContext);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { queryParams } = useQueryParams(INITIAL_QUERY_PARAMS);
  const classes = useStyles();

  // Get active category ID from route params
  const activeCategoryId = useMemo(() => {
    const { lvl1, lvl2, lvl3, lvl4, lvl5 } = routeParams;
    return lvl5 || lvl4 || lvl3 || lvl2 || lvl1 || undefined;
  }, [routeParams]);

  // Build breadcrumbs query variables
  const breadcrumbsQueryVariables = useMemo(() => {
    return {
      first: 1000,
      where: {
        brandKey_in: [BrandKey.BONE, brandKey],
        level_in: [1, 2, 3, 4, 5], // Include all levels to find any category
        ...(format && { format }),
      },
    };
  }, [brandKey, format]);

  const { data: breadcrumbsData } = useQuery(GET_PAGE_CATEGORIES, {
    variables: breadcrumbsQueryVariables,
    skip: !format || !brandKey,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: false,
    errorPolicy: 'all',
    context: {
      queryKey: `breadcrumbs-${brandKey}-${format}`,
    },
  });

  // Derive activeCategory from breadcrumbs data and route params
  const activeCategory = useMemo(() => {
    if (!breadcrumbsData?.categories) {
      return null;
    }

    // If we have a specific category ID from URL, use that
    if (activeCategoryId) {
      const found = breadcrumbsData.categories.find(
        (cat: any) => cat.id === activeCategoryId && cat.brandKey === brandKey,
      );

      if (found) {
        return {
          ...found,
          path: found.title,
        };
      }
    }

    // If no specific category ID (root level), use the default level 2 category
    const defaultLevel2Category = breadcrumbsData.categories.find(
      (cat: any) =>
        cat.level === 2 &&
        cat.format.toLowerCase() === format?.toLowerCase() &&
        cat.brandKey === brandKey,
    );

    if (defaultLevel2Category) {
      return {
        ...defaultLevel2Category,
        path: defaultLevel2Category.title,
      };
    }

    return null;
  }, [activeCategoryId, breadcrumbsData?.categories, brandKey, format]);

  const normalizedFilterStatuses = useMemo(() => {
    const rawFilterStatus: unknown = queryParams.filterStatus;

    if (Array.isArray(rawFilterStatus)) {
      return rawFilterStatus.filter(
        (status: unknown): status is CategoryStatus =>
          VALID_CATEGORY_STATUSES.has(status as CategoryStatus),
      );
    }

    if (typeof rawFilterStatus === 'string' && rawFilterStatus.length > 0) {
      return rawFilterStatus
        .split(',')
        .map((status: string) => status.trim())
        .filter((status: string): status is CategoryStatus =>
          VALID_CATEGORY_STATUSES.has(status as CategoryStatus),
        );
    }

    return [] as CategoryStatus[];
  }, [queryParams.filterStatus]);

  // Build main list query variables
  const mainListQueryVariables = useMemo(() => {
    const targetLevel = activeCategory ? activeCategory.level + 1 : 3;
    const baseWhere = {
      brandKey_in: brandKey ? [brandKey] : [],
      level: targetLevel,
      ...(format && { format }),
      status_in: [
        CategoryStatus.Published,
        CategoryStatus.Scheduled,
        CategoryStatus.Draft,
      ],
    };

    const filters = {
      ...(normalizedFilterStatuses.length > 0
        ? { status_in: normalizedFilterStatuses }
        : {}),
    };

    return {
      first: ITEMS_PER_PAGE,
      where: activeCategory
        ? { ...baseWhere, parent: { id: activeCategory.id }, ...filters }
        : { ...baseWhere, ...filters },
      skip: 0,
    };
  }, [activeCategory, brandKey, format, normalizedFilterStatuses]);

  // Main list query with pagination
  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(
    GET_PAGE_CATEGORIES,
    {
      variables: mainListQueryVariables,
      skip:
        !brandKey ||
        normalizedFilterStatuses.length === 0 ||
        !breadcrumbsData?.categories, // Wait for breadcrumbs data
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      context: {
        queryKey: `main-list-${brandKey}-${format}-${
          activeCategory?.id || 'root'
        }-${JSON.stringify(normalizedFilterStatuses)}`,
      },
    },
  );

  const activeFilterQuantity = useMemo(
    () => getActiveFilterQuantity(queryParams),
    [queryParams],
  );

  // Only track fetchMore loading for pagination
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;

  // Show loader while waiting for breadcrumbs data
  const isWaitingForBreadcrumbs = !breadcrumbsData?.categories;

  const hasCategoryCreationRights = canCreateCategory();

  // Transform the raw data to add path and other required fields
  const transformedCategories = useMemo(() => {
    if (!data?.categories) return [];
    return getBrandCategoriesWithPath(data.categories);
  }, [data?.categories]);

  // State for infinite scrolling
  const [hasMore, setHasMore] = useState(true);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Handle fetch more for infinite scrolling
  const handleFetchMore = useCallback(async () => {
    if (isFetchingMore || !hasMore || !data?.categories) return;

    // Additional check: if we have no categories, don't fetch more
    if (data.categories.length === 0) return;

    try {
      await fetchMore({
        variables: {
          skip: data.categories.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.categories) return prev;

          // If we got no new results, we've reached the end
          if (fetchMoreResult.categories.length === 0) {
            setHasMore(false);
            return prev;
          }

          return {
            ...prev,
            categories: [...prev.categories, ...fetchMoreResult.categories],
          };
        },
      });
    } catch (fetchError) {
      console.error('Error fetching more categories:', fetchError);
    }
  }, [isFetchingMore, hasMore, data?.categories, fetchMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore) {
          handleFetchMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '300px', // Trigger 300px before the element comes into view
      },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [handleFetchMore, hasMore, isFetchingMore]);

  // Refetch when activeCategory changes (navigation to sub-level)
  useEffect(() => {
    if (activeCategory && brandKey) {
      // Reset hasMore when navigating to a new level
      setHasMore(true);
      // The query will automatically refetch when variables change
    }
  }, [activeCategory, brandKey]);

  // Update hasMore based on data
  useEffect(() => {
    if (data?.categories) {
      // If we received fewer items than requested, we've reached the end
      setHasMore(data.categories.length >= ITEMS_PER_PAGE);
    }
  }, [data?.categories]);

  return (
    <>
      {/* Show loader while waiting for breadcrumbs data */}
      {isWaitingForBreadcrumbs && (
        <div className={classes.wrapperCenter}>
          <CircularProgress size={60} color="primary" />
        </div>
      )}

      {!isWaitingForBreadcrumbs && (
        <>
          <div className={classes.wrapperTopList}>
            <IsolatedBreadCrumbs breadcrumbsData={breadcrumbsData} />
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <CreationRightsWrapper canCreate={hasCategoryCreationRights}>
                <Chip
                  color="primary"
                  icon={<Add />}
                  label="Créer une page"
                  onClick={() => {
                    const search = new URLSearchParams({
                      from: 'Breadcrumbs',
                    }).toString();

                    navigateTo({
                      pathname: paths.PAGE_CREATE,
                      routeParams: {
                        ...routeParams,
                        lvl1: activeCategory?.id || undefined,
                      },
                      opts: { search },
                    });
                  }}
                  style={{
                    minWidth: '140px',
                    position: 'relative',
                    top: '-3px',
                  }}
                />
              </CreationRightsWrapper>
              <Filter
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
              />
            </div>
          </div>

          {/* API Error */}
          {error && (
            <div className={classes.wrapperCenter}>
              <Error color="primary" fontSize="large" />
              <Typography color="primary" variant="h5">
                {error?.message || ERROR_HAPPENED_TEXT}
              </Typography>
              <br />
              <Button
                onClick={() => {
                  refetch();
                }}
                variant="outlined"
              >
                {ERROR_BUTTON_RELOAD_LABEL}
              </Button>
            </div>
          )}

          {/* Empty API result */}
          {!loading &&
            !error &&
            (!data?.categories || data.categories.length === 0) && (
              <div className={classes.wrapperCenter}>
                <Typography variant="h5" color="primary">
                  {NO_RESULT_TEXT}
                </Typography>

                {!!activeFilterQuantity && (
                  <Chip
                    color="primary"
                    className={classes.activeFilterBadge}
                    icon={<FilterList />}
                    label={`${activeFilterQuantity} ${pluralize(
                      ACTIVE_FILTER_PREFIX,
                      activeFilterQuantity,
                    )} ${pluralize(
                      ACTIVE_FILTER_SUFFIX,
                      activeFilterQuantity,
                    )}`}
                    onClick={() => {
                      setIsFilterOpen(true);
                    }}
                  />
                )}
              </div>
            )}

          {/* Categories List */}
          <div className={classes.categoriesList}>
            {transformedCategories && transformedCategories.length > 0 ? (
              <>
                {transformedCategories.map((category: CategoryList) => (
                  <div key={category.id} className={classes.categoryItem}>
                    <CategoryCard category={category} />
                  </div>
                ))}

                {/* Load more trigger */}
                {hasMore && (
                  <div ref={loadMoreRef} className={classes.loadMoreTrigger}>
                    {isFetchingMore ? (
                      <Box className={classes.loadingContainer}>
                        <CircularProgress color="primary" size={40} />
                      </Box>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <div className={classes.noResults}>
                {loading && (
                  <Box className={classes.loadingContainer}>
                    <CircularProgress color="primary" size={40} />
                  </Box>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export { List };
